// The parent's bus card on home (docs/spec/transport.md slice 3): each of their children riding a
// bus today, and where things stand. Refreshes with the live socket (useGuardian().version).
import { useEffect, useRef, useState } from "react";
import { Badge, Button, Icon, Sheet, Spinner, TextField, useToast } from "@vavr/ui";
import { api, navigate, time } from "@vavr/screens/shared";
import { lngLat, RunMap, type RunMapStop } from "./runmap.tsx";
import { clock, Status, type Tone } from "./status.tsx";
import { G, useGuardian } from "@vavr/screens/guardian";

interface Ride {
  child_id: string; first: string; route: { id: string; name: string; kind: "am" | "pm" | "trip" };
  /** The parent said not on the bus today (late: after the school's cut-off, the office confirms). */
  not_riding?: { late: boolean; why?: "parent" | "absent" } | null;
  stop: { name: string; time: string | null; lat: number | null; lng: number | null } | null; school_time: string | null;
  school: { lat: number; lng: number } | null;
  /** Which bus today: its name and plate; `replaces` when it stands in for the usual one (PMU-232). */
  vehicle?: { name: string; plate: string | null; replaces: { name: string; reason: string | null; until: string } | null } | null;
  /** Where the bus is: only while it is coming for this child or carrying them, and fresh. */
  bus: { lat: number; lng: number; at: number; heading?: number | null } | null;
  /** Where the bus has been since the child got on ([lng, lat]); only their own ride. */
  trail?: [number, number][];
  run: { id?: string; started_at: number; finished_at: number | null } | null;
  status: "waiting" | "on" | "off" | "absent" | "held"; at: number | null;
  /** When the bus reaches the place that matters (the child's stop; school on the morning bus with the child). */
  eta_at: number | null;
  /** Stops the bus makes before that place; where it is going next (never another family's stop by name). */
  stops_before?: number | null; going?: "school" | "your_stop" | "route" | null;
}
interface Summary { pill: string; tone: Tone; hero: string; sub: string | null; live: boolean }

const inMin = (at: number, now: number) => { const m = Math.round((at - now) / 60_000); return m <= 0 ? "now" : m === 1 ? "in 1 min" : `in ${m} min`; };
const ago = (at: number, now: number) => { const s = Math.max(0, Math.round((now - at) / 1000)); return s < 60 ? `${s} s ago` : `${Math.round(s / 60)} min ago`; };

/** What the parent needs at a glance: a status, the one line that matters (when), and where the bus is. */
export function summary(r: Ride, tz: string, now: number): Summary {
  const am = r.route.kind === "am";
  const place = am && r.status === "on" ? "school" : r.stop?.name ?? "your stop";
  const where = r.stops_before == null ? null : r.stops_before === 0 ? (r.going === "school" ? "Next stop: school" : "Next stop is yours") : `${r.stops_before} ${r.stops_before === 1 ? "stop" : "stops"} before yours`;
  const seen = r.bus ? `updated ${ago(r.bus.at, now)}` : null;
  const sub = (...x: (string | null)[]) => x.filter(Boolean).join(" · ") || null;
  if (r.not_riding && r.status === "waiting") {
    if (r.not_riding.why === "absent") return { pill: "Absent today", tone: "neutral", live: false, hero: `${r.first} is absent, so not on the bus`, sub: "The driver knows." };
    return { pill: "Not riding today", tone: "neutral", live: false, hero: `${r.first} is not on the bus today`, sub: r.not_riding.late ? "Sent after the school's cut-off: the office will confirm." : "The driver knows." };
  }
  switch (r.status) {
    case "on":
      return { pill: "On the bus", tone: "info", live: true, hero: r.eta_at ? `${am ? "At school" : "Home stop"} ${inMin(r.eta_at, now)} · ${time(r.eta_at, tz)}` : `On the way to ${place}`,
        sub: sub(r.at ? `Got on ${time(r.at, tz)}` : null, where, seen) };
    case "off": return { pill: am ? "At school" : "Home", tone: "success", live: false, hero: `${am ? "Arrived at school" : `Dropped off at ${r.stop?.name ?? "the stop"}`} · ${time(r.at!, tz)}`, sub: null };
    case "absent": return { pill: "Not at the stop", tone: "warning", live: false, hero: `The bus did not pick ${r.first} up`, sub: `At ${r.stop?.name ?? "the stop"}. Tell the school if ${r.first} is not coming.` };
    case "held": return { pill: "Staying on the bus", tone: "danger", live: false, hero: "No approved adult was at the stop", sub: "The bus is taking them back to school. Please call the school." };
    default:
      if (r.run?.finished_at) return { pill: "Bus finished", tone: "neutral", live: false, hero: `Not on today's ${am ? "morning" : "afternoon"} bus`, sub: null };
      if (r.run) {
        // The bus is out: in the morning it is coming to the stop; in the afternoon, to school first.
        const pill = am ? "Bus on its way" : r.going === "school" ? "Bus heading to school" : "Bus on its way";
        const hero = r.eta_at ? (am ? `Bus at your stop ${inMin(r.eta_at, now)} · ${time(r.eta_at, tz)}` : `Home stop about ${time(r.eta_at, tz)}`) : am ? `Pick-up at ${r.stop?.name ?? "the stop"}` : `Rides home to ${r.stop?.name ?? "the stop"}`;
        return { pill, tone: "info", live: true, hero, sub: sub(am ? where : r.going === "school" ? `${r.first} gets on at school` : where, seen) };
      }
      return { pill: "Today", tone: "neutral", live: false, hero: am ? `Pick-up at ${r.stop?.name ?? "the stop"}${r.stop?.time ? `, ${clock(r.stop.time)}` : ""}` : `Rides home to ${r.stop?.name ?? "the stop"}${r.stop?.time ? `, about ${clock(r.stop.time)}` : ""}`, sub: null };
  }
}

const rideStops = (ride: Ride): RunMapStop[] => [
  ...(ride.stop ? [{ key: "stop", name: ride.stop.name, lat: ride.stop.lat, lng: ride.stop.lng, school: false, n: undefined }] : []),
  ...(ride.school ? [{ key: "school", name: "School", lat: ride.school.lat, lng: ride.school.lng, school: true }] : []),
];

/** "Bus 12 (plate TWL 012) instead of Bus 7 today" / "... until Fri, Oct 2": for a bus standing in. */
function swapLine(v: NonNullable<Ride["vehicle"]>, tz: string) {
  const r = v.replaces!;
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const until = r.until === today ? "today" : `until ${new Date(`${r.until}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })}`;
  return `${v.name}${v.plate ? ` (plate ${v.plate})` : ""} instead of ${r.name} ${until}.`;
}

export function BusCard() {
  const { overview, version } = useGuardian();
  const [rides, setRides] = useState<{ tz: string; site: string; ride: Ride }[]>([]);
  const [tick, setTick] = useState(0);
  const now = Date.now();
  useEffect(() => {
    let alive = true;
    Promise.all(overview.sites.map((s) => api<{ rides: Ride[] }>("GET", `/api/v1/guardian/sites/${s.site.id}/transport/today`)
      .then((d) => d.rides.map((ride) => ({ tz: s.site.tz, site: s.site.id, ride })), () => [])))
      .then((all) => { if (alive) setRides(all.flat()); });
    return () => { alive = false; };
  }, [overview, version, tick]);
  // While a bus is out for one of the children: fresh position and arrival every 15 s.
  const anyLive = rides.some(({ ride, tz }) => summary(ride, tz, now).live);
  useEffect(() => { if (!anyLive) return; const i = setInterval(() => setTick((t) => t + 1), 15_000); return () => clearInterval(i); }, [anyLive]);
  if (!rides.length) return null;
  return (
    <section aria-labelledby="pmt-bus" className="pmt-buscards">
      <h2 id="pmt-bus" className="pmu-g-section">Bus today</h2>
      {rides.map(({ tz, site, ride }) => {
        const sm = summary(ride, tz, now);
        const key = `${ride.child_id}:${ride.route.id}`;
        return (
          <article key={key} className="pmt-buscard" data-tone={sm.tone}>
            <button type="button" className="pmt-buscard__hit" aria-label={`${ride.first}'s bus: ${sm.pill}. ${sm.hero}. Open the full view.`}
              onClick={() => navigate(`${G}/bus/${site}/${ride.child_id}/${ride.route.id}`)} />
            <header className="pmt-buscard__head">
              <span className="pmt-buscard__icon" aria-hidden="true"><Icon name="bus" /></span>
              <span className="pmt-buscard__who"><b>{ride.first}</b><span className="pmu-small pmu-muted">{ride.route.name}{ride.vehicle && !ride.vehicle.replaces ? ` · ${ride.vehicle.name}` : ""}</span></span>
            </header>
            {ride.vehicle?.replaces && <p className="pmt-swap"><b>A different bus:</b> {swapLine(ride.vehicle, tz)}</p>}
            <Status tone={sm.tone} live={sm.live}>{sm.pill}</Status>
            <p className="pmt-buscard__hero">{sm.hero}</p>
            {sm.sub && <p className="pmt-buscard__sub pmu-small">{sm.sub}</p>}
            {/* Always a map: their stop and the school, and the bus while it is coming or carrying them. */}
            {(ride.bus || rideStops(ride).some((x) => x.lat != null)) && (
              <div className="pmt-buscard__map" aria-hidden="true">
                <RunMap id={`card-${key}`} height={160} follow={!!ride.bus} label={`The bus on the map, for ${ride.first}`} trail={ride.trail ?? []} stops={rideStops(ride)}
                  bus={ride.bus ? { lat: ride.bus.lat, lng: ride.bus.lng, heading: ride.bus.heading, title: "The bus" } : null} />
              </div>
            )}
            <span className="pmt-buscard__more pmu-small">{sm.live ? "Live view" : "Details"} <Icon name="chevron-right" /></span>
          </article>
        );
      })}
      <button type="button" className="pmt-buscards__past" onClick={() => navigate(`${G}/rides`)}><Icon name="clock" /> Past rides <Icon name="chevron-right" /></button>
    </section>
  );
}

/** The bus's page (/guardian/bus/:site/:child/:route): the child's ride today, refreshed every 15 s while the bus is out. */
export function BusPage({ site, child, route }: { site: string; child: string; route: string }) {
  const { overview } = useGuardian();
  const tz = overview.sites.find((s) => s.site.id === site)?.site.tz ?? "America/New_York";
  const [ride, setRide] = useState<Ride | null | "none">(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    api<{ rides: Ride[] }>("GET", `/api/v1/guardian/sites/${site}/transport/today`)
      .then((d) => alive && setRide(d.rides.find((r) => r.child_id === child && r.route.id === route) ?? "none"), () => alive && setRide((r) => r ?? "none"));
    return () => { alive = false; };
  }, [site, child, route, tick]);
  useEffect(() => { const i = setInterval(() => setTick((t) => t + 1), 15_000); return () => clearInterval(i); }, []);
  if (ride === null) return <div className="pmu-center"><Spinner label="Loading the bus" /></div>;
  if (ride === "none") return <p className="pmu-muted">No bus for this child today.</p>;
  return <LiveRide tz={tz} ride={ride} now={Date.now()} site={site} onChanged={() => setTick((t) => t + 1)} onRecord={() => navigate(`${G}/rides/${site}/${ride.run!.id}/${child}`)} />;
}

/**
 * The full view, the whole screen like a ride app: the map fills it, a back arrow on top, and the
 * ride's status and details in a panel at the bottom. Nothing to scroll.
 */
function LiveRide({ tz, ride, now, site, onRecord, onChanged }: { tz: string; ride: Ride; now: number; site: string; onRecord: () => void; onChanged: () => void }) {
  const sm = summary(ride, tz, now);
  const am = ride.route.kind === "am";
  const back = () => navigate(G, true);
  // The panel and the top bar lie over the map: the bus is kept in the part between them.
  const panel = useRef<HTMLElement>(null), top = useRef<HTMLElement>(null);
  const [inset, setInset] = useState<{ top: number; bottom: number }>({ top: 0, bottom: 0 });
  useEffect(() => {
    const measure = () => setInset({ top: Math.round(top.current?.getBoundingClientRect().bottom ?? 0), bottom: Math.round(panel.current?.offsetHeight ?? 0) });
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (panel.current) ro?.observe(panel.current);
    return () => ro?.disconnect();
  }, []);
  return (
    <div className="pmt-livepage">
      <div className="pmt-livepage__map">
        <RunMap id={`live-${ride.child_id}`} height="100%" follow={!!ride.bus} inset={inset} label={`${ride.first}'s bus on the map`} trail={ride.trail ?? []} stops={rideStops(ride)}
          bus={ride.bus ? { lat: ride.bus.lat, lng: ride.bus.lng, heading: ride.bus.heading, title: "The bus" } : null} />
      </div>
      <header className="pmt-livepage__top" ref={top}>
        <button type="button" className="pmt-livepage__back" onClick={back} aria-label={`Back. ${ride.first}'s bus`}>
          <Icon name="chevron-left" /><span>{ride.first}'s bus</span>
        </button>
      </header>
      <section className="pmt-livepage__panel" aria-label="The ride now" ref={panel}>
        <div className="pmt-livepage__status">
          <Status tone={sm.tone} live={sm.live}>{sm.pill}</Status>
          <p className="pmt-buscard__hero">{sm.hero}</p>
          {sm.sub && <p className="pmu-small pmu-muted">{sm.sub}</p>}
        </div>
        {ride.vehicle?.replaces && <p className="pmt-swap"><b>A different bus:</b> {swapLine(ride.vehicle, tz)}{ride.vehicle.replaces.reason ? ` ${ride.vehicle.replaces.reason}` : ""}</p>}
        <dl className="pmt-livefacts pmt-livepage__facts">
          {ride.vehicle && <div><dt>Bus</dt><dd>{ride.vehicle.name}{ride.vehicle.plate ? ` · ${ride.vehicle.plate}` : ""}</dd></div>}
          <div><dt>{am ? "Picked up at" : "Drops off at"}</dt><dd>{ride.stop?.name ?? "—"}</dd></div>
          {ride.stop?.time && <div><dt>Planned</dt><dd>{clock(ride.stop.time)}</dd></div>}
          {ride.eta_at && <div><dt>Expected</dt><dd>{time(ride.eta_at, tz)}</dd></div>}
          {ride.stops_before != null && <div><dt>Stops before yours</dt><dd>{ride.stops_before}</dd></div>}
          {ride.at && ride.status === "on" && <div><dt>Got on</dt><dd>{time(ride.at, tz)}</dd></div>}
          {ride.bus && <div><dt>Bus last seen</dt><dd>{ago(ride.bus.at, now)}</dd></div>}
        </dl>
        {ride.status === "waiting" && !ride.run?.finished_at && ride.not_riding?.why !== "absent" && <SkipToday site={site} ride={ride} onChanged={onChanged} />}
        {ride.route.kind !== "am" && <MeetChoice site={site} child={ride.child_id} first={ride.first} />}
        {ride.run?.id && ride.status !== "waiting" && <Button size="sm" variant="secondary" onClick={onRecord}>The whole ride so far</Button>}
      </section>
    </div>
  );
}

interface RideRecord {
  ride: { run_id: string; date: string; route: string; kind: string; vehicle: string | null; driver: string | null; child: { id: string; first: string }; on_at: number | null; off_at: number | null; finished: boolean };
  stops: { name: string; time: string | null; school: boolean; lat: number | null; lng: number | null }[];
  events: { at: number; type: string; stop: string | null; adult: string | null; flags: string[] }[];
  trail: { at: number; lat: number; lng: number; heading: number | null }[];
}
const RIDE_EVENT: Record<string, string> = { board: "Got on the bus", alight: "Got off the bus", absent: "Not at the stop", held: "Kept on the bus" };

/** One ride of the parent's own child: the way the bus went while they were on it, and when they got on and off. */
function RideView({ site, tz, run, child }: { site: string; tz: string; run: string; child: string }) {
  const [r, setR] = useState<RideRecord | null | "error">(null);
  useEffect(() => {
    let live = true;
    api<RideRecord>("GET", `/api/v1/guardian/sites/${site}/transport/rides/${run}?child=${encodeURIComponent(child)}`).then((d) => live && setR(d), () => live && setR("error"));
    return () => { live = false; };
  }, [site, run, child]);
  if (r === null) return <Spinner label="Loading the ride" />;
  if (r === "error") return <p className="pmu-muted">This ride is not available.</p>;
  const last = r.trail.at(-1);
  return (
    <div className="pmu-stack">
      <p><b>{r.ride.child.first}</b> · {r.ride.route} · {r.ride.date}</p>
      <RunMap id={`ride-${run}`} height={260} label={`${r.ride.child.first}'s ride on the map`} trail={lngLat(r.trail)}
        stops={r.stops.map((x, i) => ({ key: String(i), name: x.name, lat: x.lat, lng: x.lng, school: x.school }))}
        bus={!r.ride.finished && !r.ride.off_at && last ? { lat: last.lat, lng: last.lng, heading: last.heading, title: "The bus" } : null} />
      <ol className="pmt-timeline">
        {r.events.map((e, i) => (
          <li key={i} data-type={e.type}>
            <span className="pmt-timeline__time">{time(e.at, tz)}</span>
            <span>{RIDE_EVENT[e.type] ?? e.type}{e.stop ? ` at ${e.stop}` : ""}{e.adult ? `, handed to ${e.adult}` : ""}</span>
          </li>
        ))}
      </ol>
      <p className="pmu-small pmu-muted">{r.ride.vehicle ?? "The bus"}{r.ride.driver ? ` · driven by ${r.ride.driver}` : ""}. The map shows only the part of the route while {r.ride.child.first} was on board.</p>
    </div>
  );
}

/** One ride's page (/guardian/rides/:site/:run/:child). */
export function RidePage({ site, run, child }: { site: string; run: string; child: string }) {
  const { overview } = useGuardian();
  return <RideView site={site} run={run} child={child} tz={overview.sites.find((s) => s.site.id === site)?.site.tz ?? "America/New_York"} />;
}

/** Past rides' page (/guardian/rides). */
export function RidesPage() {
  return <PastRides onOpen={(r) => navigate(`${G}/rides/${r.site}/${r.run}/${r.child}`)} />;
}

/** The last rides of the parent's children, newest first. */
function PastRides({ onOpen }: { onOpen: (r: { site: string; tz: string; run: string; child: string }) => void }) {
  const { overview } = useGuardian();
  const [list, setList] = useState<{ site: string; tz: string; ride: { run_id: string; child_id: string; first: string; date: string; route: string; on_at: number | null; off_at: number | null; status: string } }[] | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all(overview.sites.map((s) => api<{ rides: { run_id: string; child_id: string; first: string; date: string; route: string; on_at: number | null; off_at: number | null; status: string }[] }>("GET", `/api/v1/guardian/sites/${s.site.id}/transport/rides`)
      .then((d) => d.rides.map((ride) => ({ site: s.site.id, tz: s.site.tz, ride })), () => [])))
      .then((all) => live && setList(all.flat().sort((a, b) => (b.ride.on_at ?? 0) - (a.ride.on_at ?? 0))));
    return () => { live = false; };
  }, [overview]);
  if (!list) return <Spinner label="Loading rides" />;
  if (!list.length) return <p className="pmu-muted">No bus rides in the last 60 days.</p>;
  return (
    <ul className="pmt-buslist">
      {list.map(({ site, tz, ride }) => (
        <li key={`${ride.run_id}:${ride.child_id}`}>
          <button type="button" className="pmt-buslist__row" onClick={() => onOpen({ site, tz, run: ride.run_id, child: ride.child_id })}>
            <span className="pmt-buslist__main"><b>{ride.first} · {ride.route}</b>
              <span className="pmu-small pmu-muted">{ride.date}{ride.on_at ? ` · on ${time(ride.on_at, tz)}` : ""}{ride.off_at ? ` · off ${time(ride.off_at, tz)}` : ""}</span></span>
            <Badge tone={ride.status === "alight" ? "success" : ride.status === "absent" ? "warning" : ride.status === "held" ? "danger" : "info"}>{ride.status === "alight" ? "Done" : ride.status === "absent" ? "Missed" : ride.status === "held" ? "Kept on bus" : "On board"}</Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

interface Meet { meet_required: boolean; set_by: "school" | "parent"; reason: string | null; school_default: boolean; grade: string | null; walk_from: string | null }

/**
 * Who meets the child at their stop (school rules): the school's rule by grade, which the parent can
 * change either way with a reason the school and the driver see.
 */
function MeetChoice({ site, child, first }: { site: string; child: string; first: string }) {
  const url = `/api/v1/guardian/sites/${site}/transport/children/${child}/meet`;
  const [m, setM] = useState<Meet | null>(null);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  useEffect(() => { let live = true; api<Meet>("GET", url).then((d) => live && setM(d), () => undefined); return () => { live = false; }; }, [url]);
  if (!m) return null;
  const differs = pick !== m.school_default;
  const rule = m.walk_from ? `Your school lets children in grade ${m.walk_from} and up walk home from the stop.` : "Your school asks for an adult to meet every child at the stop.";
  const save = async () => {
    setBusy(true);
    try { setM({ ...m, ...(await api<Partial<Meet>>("PUT", url, { meet_required: pick, reason: differs ? reason : undefined })) }); setOpen(false); toast.show({ title: "Saved. The school and the driver can see it.", tone: "success" }); }
    catch (e) { toast.show({ title: (e as Error).message, tone: "danger" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="pmt-meet">
      <div className="pmt-meet__text">
        <span className="pmt-meet__label">At the stop</span>
        <b>{m.meet_required ? `An adult meets ${first}` : `${first} may walk home`}</b>
        {m.set_by === "parent" && <small>Your choice{m.reason ? `: ${m.reason}` : ""}</small>}
      </div>
      <Button size="sm" variant="secondary" onClick={() => { setPick(m.meet_required); setReason(m.reason ?? ""); setOpen(true); }}>Change</Button>
      <Sheet open={open} onOpenChange={setOpen} title={`At ${first}'s stop`} description={rule}>
        <div className="pmu-stack">
          <div className="pmt-meet__options" role="radiogroup" aria-label={`Who meets ${first}`}>
            <button type="button" role="radio" aria-checked={pick} className="pmt-meet__option" onClick={() => setPick(true)}>
              <b>An adult meets {first}</b><span>The driver hands {first} only to an adult on your list. If nobody is there, {first} stays on the bus and goes back to school.</span>
            </button>
            <button type="button" role="radio" aria-checked={!pick} className="pmt-meet__option" onClick={() => setPick(false)}>
              <b>{first} may walk home</b><span>The driver lets {first} off at the stop without an adult.</span>
            </button>
          </div>
          {differs && <TextField label="Why? The school and the driver see this" required value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder={pick ? "Needs to be met, e.g. a new route" : "e.g. We live across the street"} />}
          <Button fullWidth loading={busy} disabled={differs && reason.trim().length < 3} onClick={save}>Save</Button>
        </div>
      </Sheet>
    </div>
  );
}

/**
 * "Not on the bus today" (school rules): the parent takes the child off today's afternoon run. Before
 * the school's cut-off the driver just knows; after it the office is told too, to confirm.
 */
function SkipToday({ site, ride, onChanged }: { site: string; ride: Ride; onChanged: () => void }) {
  const url = `/api/v1/guardian/sites/${site}/transport/routes/${ride.route.id}/skip`;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const run = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await f(); toast.show({ title: ok, tone: "success" }); setOpen(false); onChanged(); }
    catch (e) { toast.show({ title: (e as Error).message, tone: "danger" }); }
    finally { setBusy(false); }
  };
  if (ride.not_riding) {
    return <Button size="sm" variant="secondary" loading={busy} onClick={() => run(() => api("DELETE", `${url}/${ride.child_id}`), `${ride.first} is back on the bus today`)}>Put {ride.first} back on the bus</Button>;
  }
  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Not on the bus today</Button>
      <Sheet open={open} onOpenChange={setOpen} title={`${ride.first} is not on the ${ride.route.kind === "am" ? "morning" : "afternoon"} bus today`} description="The driver will not wait for them. After your school's cut-off time the office is told too, to confirm.">
        <div className="pmu-stack">
          <TextField label="Anything the school should know (optional)" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="I'm collecting them myself" />
          <Button fullWidth loading={busy} onClick={() => run(async () => { const r = await api<{ late: boolean; close: string | null }>("POST", url, { child_id: ride.child_id, reason: reason || undefined }); if (r.late) toast.show({ title: `After ${r.close}: the office will confirm.`, tone: "info" }); }, "The driver knows")}>Confirm</Button>
        </div>
      </Sheet>
    </>
  );
}
